import { Box, Checkbox, TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import React from 'react';
import { useBetween } from 'use-between';
import { IDiameterCheckboxDropdown } from '../interfaces/diameter-checkbox-dropdown.interface';
import { ProductFiltersUseBetween } from '../use-between/product-filters-use-between';
import { Utils } from '../../../shared/constants/utilities.constants';
import {
    getDeletedOrAddedItem,
    getItemsAfterDelete,
    getItemsAfterInsert,
    returnSelectedItems,
} from './diameter-checkbox.service';
import { border } from '../../../shared/dropdown/dropdown.service';
import { useTranslation } from '../../../i18n/useTranslation';

export interface ICheckboxDropdownInput {
    data: IDiameterCheckboxDropdown[];
    isFromDn: boolean;
    showSelectAll?: boolean;
}

const SELECT_ALL_INDEX = -1;
const selectAllOption: IDiameterCheckboxDropdown = {
    index: SELECT_ALL_INDEX,
    isSelected: false,
    config: { name: '', value: 0, index: SELECT_ALL_INDEX },
};

const DiameterCheckboxDropdown: React.FC<ICheckboxDropdownInput> = ({ data, isFromDn, showSelectAll }): JSX.Element => {
    const { t } = useTranslation('translation');
    const { selectedNpsAndDnDiameter, setSelectedNpsAndDnDiameter } = useBetween(ProductFiltersUseBetween);
    const { setDiameterRangeFromTo } = useBetween(ProductFiltersUseBetween);
    const { diameterMin } = useBetween(ProductFiltersUseBetween);
    const isAllSelected = data.length > 0 && selectedNpsAndDnDiameter.length === data.length;

    const updateDiameterMm = (selectedItems: IDiameterCheckboxDropdown[]): void => {
        if (selectedItems.length === 0) {
            setDiameterRangeFromTo([diameterMin, diameterMin]);
            setSelectedNpsAndDnDiameter(returnSelectedItems([data[0]]));
        } else if (selectedItems.length === 1) {
            setDiameterRangeFromTo([selectedItems[0].config.value, selectedItems[0].config.value]);
        } else {
            setDiameterRangeFromTo([
                selectedItems[0].config.value,
                selectedItems[selectedItems.length - 1].config.value,
            ]);
        }
    };

    const change = (selectedValues: IDiameterCheckboxDropdown[], isClear: boolean) => {
        let selectedItems: IDiameterCheckboxDropdown[] = [];
        if (!isClear) {
            const deleteItem: IDiameterCheckboxDropdown | null = getDeletedOrAddedItem(
                selectedNpsAndDnDiameter,
                selectedValues
            );
            const addedItem: IDiameterCheckboxDropdown | null = getDeletedOrAddedItem(
                selectedValues,
                selectedNpsAndDnDiameter
            );
            if (deleteItem) {
                selectedItems = getItemsAfterDelete(Utils.clone(selectedNpsAndDnDiameter), deleteItem);
                setSelectedNpsAndDnDiameter(selectedItems);
            } else if (addedItem) {
                selectedItems = getItemsAfterInsert(
                    Utils.clone(data),
                    Utils.clone(selectedNpsAndDnDiameter),
                    addedItem
                );
                setSelectedNpsAndDnDiameter(selectedItems);
            }
        } else {
            setSelectedNpsAndDnDiameter(selectedItems);
        }
        updateDiameterMm(selectedItems);
    };

    const isSelected = (option: IDiameterCheckboxDropdown): boolean => {
        if (option.index === SELECT_ALL_INDEX) {
            return isAllSelected;
        }
        return selectedNpsAndDnDiameter.some((x) => x.index === option.index);
    };

    const selectAll = () => {
        const next = isAllSelected ? [] : returnSelectedItems(Utils.clone(data));
        setSelectedNpsAndDnDiameter(next);
        updateDiameterMm(next);
    };

    const options = showSelectAll ? [selectAllOption, ...data] : data;

    return (
        <Autocomplete
            multiple
            id="checkboxes-tags-demo"
            options={options}
            isOptionEqualToValue={(option, value) => option.index === value.index}
            disableCloseOnSelect
            style={{ width: '300px' }}
            value={selectedNpsAndDnDiameter}
            getOptionLabel={(option) =>
                option.index === SELECT_ALL_INDEX
                    ? t('SELECT_ALL')
                    : isFromDn
                      ? option.config.value.toString()
                      : option.config.name
            }
            onChange={(_, values, reason) => {
                if (values.some((v) => v.index === SELECT_ALL_INDEX)) {
                    selectAll();
                    return;
                }
                change(values, reason === 'clear');
            }}
            sx={{
                '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                        border: { border },
                        padding: '10px',
                    },
                    '&.Mui-focused fieldset': {
                        border: { border },
                    },
                    '&:hover fieldset': {
                        border: { border },
                    },
                },
                '& .MuiAutocomplete-input': {
                    padding: '0px !important',
                },
            }}
            renderOption={(props, option) => (
                <Box component={'li'} {...props}>
                    <Checkbox
                        sx={{
                            '&.Mui-checked': {
                                color: '#821818',
                            },
                        }}
                        checked={isSelected(option)}
                    />
                    {option.index === SELECT_ALL_INDEX
                        ? t('SELECT_ALL')
                        : isFromDn
                          ? option.config.value.toString()
                          : option.config.name}
                </Box>
            )}
            renderInput={(params) => <TextField {...params} />}
        />
    );
};
export default DiameterCheckboxDropdown;
